import { resolveCodDisplayCurrency } from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PayoutItem } from '../models/PayoutDetail';

const FIELD_TO_API_COLUMN: Record<string, string> = {
  'order.ref': 'order.ref',
  order_id: 'order_id',
  'order.shop.name': 'order.shop.name',
  'order.courier.name': 'order.courier.name',
  'order.tracking_number': 'order.tracking_number',
  payout_type: 'payout_type',
  amount: 'amount',
  currency: 'currency',
};

const VALID_EXPORT_COLUMNS = new Set([
  'id',
  'cod_payout_id',
  'client_id',
  'order_id',
  'amount',
  'payout_type',
  'currency',
  'description',
  'created_at',
  'cod_payout.id',
  'cod_payout.total_amount',
  'cod_payout.payout_date',
  'client.name',
  'order.ref',
  'order.tracking_number',
  'order.shop.name',
  'order.courier.name',
]);

export const useAgGridColumns = () => {
  const { t } = useTranslation('codPayoutDetail');
  const teamCurrency = useCurrency();

  const columnDefs: ColDef<PayoutItem>[] = useMemo(
    () => [
      {
        field: 'order.ref',
        headerName: t('items.table.order', 'Pedido'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.ref ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium text-foreground">{params.data?.order?.ref ?? '-'}</span>
        ),
      },
      {
        field: 'order_id',
        headerName: t('items.table.order_id', 'ID Pedido'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params) => params.data?.order_id ?? params.data?.order?.id ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.order_id ?? params.data?.order?.id ?? '-'}</span>
        ),
      },
      {
        field: 'order.shop.name',
        headerName: t('items.table.shop', 'Tienda'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.order?.shop?.name ?? '-'}</span>
        ),
      },
      {
        field: 'order.courier.name',
        headerName: t('items.table.courier', 'Mensajería'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.courier?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.order?.courier?.name ?? '-'}</span>
        ),
      },
      {
        field: 'order.tracking_number',
        headerName: t('items.table.tracking', 'Tracking'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.tracking_number ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.order?.tracking_number ?? '-'}</span>
        ),
      },
      {
        field: 'payout_type',
        headerName: t('items.table.type', 'Tipo'),
        minWidth: 130,
        sortable: false,
        valueGetter: (params) => params.data?.payout_type ?? '-',
        cellRenderer: (params: any) => {
          const type = params.data?.payout_type;
          if (!type) return '-';

          const variants: Record<string, { variant: any; label: string }> = {
            advance: {
              variant: 'default',
              label: t('items.type.advance', 'Anticipo'),
            },
            settlement: {
              variant: 'success',
              label: t('items.type.settlement', 'Liquidación'),
            },
            recovery: {
              variant: 'warning',
              label: t('items.type.recovery', 'Recuperación'),
            },
          };

          const typeInfo = variants[type] || { variant: 'default', label: type };

          return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
        },
      },
      {
        field: 'amount',
        headerName: t('items.table.amount', 'Importe'),
        minWidth: 120,
        sortable: true,
        valueGetter: (params) => params.data?.amount ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.amount ?? 0;
          const row: PayoutItem | undefined = params.data;
          const currency = resolveCodDisplayCurrency(row?.currency, teamCurrency);
          const isNegative = amount < 0;

          return (
            <span className={`text-sm font-medium ${isNegative ? 'text-destructive' : 'text-foreground'}`}>
              {formatCurrencyAmount(amount, currency)}
            </span>
          );
        },
      },
    ],
    [t, teamCurrency],
  );

  // 🎯 Función para obtener las columnas en el orden del frontend, filtrando las no válidas
  const getColumnsInOrder = useCallback((): string[] => {
    return columnDefs
      .map((col) => col.field as string | undefined)
      .filter((field): field is string => !!field)
      .map((field) => FIELD_TO_API_COLUMN[field] || field)
      .filter((column) => VALID_EXPORT_COLUMNS.has(column)); // Filtrar solo columnas válidas
  }, [columnDefs]);

  return { columnDefs, getColumnsInOrder };
};
